import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { fmt, fmtDate } from '../api.ts';

/** A saved box, as the order form needs it to show and fill a line. */
export interface BoxChoice {
  preset_id: number;
  box_name: string;
  code: string | null;
  box_size: string | null;
  box_type: string | null;
  ups: number | null;
  board_name: string | null;
  last_ordered: string | null;
  last_quantity: string | null;
}

/** Lower-case, "×" read as "x", and a copy with no spaces, so "30x20" finds "30 X 20 CM". */
const norm = (s: string) => s.toLowerCase().replace(/×/g, 'x');
const squash = (s: string) => norm(s).replace(/\s+/g, '');

/** "30 X 20 CM · 3 Ply · 2 ups · Grey Back 230 · last 12 Oct, 3,000" */
function describe(b: BoxChoice) {
  return [
    b.box_size, b.box_type, b.ups ? `${b.ups} ups` : null,
    b.board_name ? b.board_name.split(' · ').slice(0, 2).join(' ') : null,
    b.last_ordered
      ? `last ${fmtDate(String(b.last_ordered).slice(0, 10))}${b.last_quantity ? `, ${fmt(b.last_quantity)}` : ''}`
      : 'saved, not ordered yet',
  ].filter(Boolean).join(' · ');
}

/**
 * The box name on an order line: type to find one of this customer's saved
 * boxes, or keep typing to name a new one.
 *
 * Unlike SearchSelect, anything typed is a valid answer -- a box never made
 * before is just a name -- so the list only helps: it finds repeats by name,
 * code or size (every word typed must appear, in any order), most recently
 * ordered first, and says "+ New box" when nothing typed matches a saved name,
 * which is what stops the same box being created twice with a different
 * spelling.
 *
 * Keyboard: arrows move, Enter picks (or keeps the new name), Escape closes.
 */
export default function BoxCombo({
  id, value, boxes, onType, onPick, disabled, placeholder,
}: {
  id?: string;
  value: string;
  boxes: BoxChoice[];
  /** Every keystroke: the line's box name is whatever is typed. */
  onType: (name: string) => void;
  /** A saved box was chosen: fill the line from it. */
  onPick: (box: BoxChoice) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [filtering, setFiltering] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  const typed = value.trim();
  const exact = boxes.some((b) => b.box_name.trim().toLowerCase() === typed.toLowerCase());
  const shown = useMemo(() => {
    const words = norm(typed).split(/\s+/).filter(Boolean);
    // Empty, or already one of the saved boxes: show them all, so the list
    // offers a change rather than the one box already chosen.
    if (words.length === 0 || exact || !filtering) return boxes;
    return boxes.filter((b) => {
      const text = `${b.box_name} ${b.code ?? ''} ${b.box_size ?? ''} ${b.box_type ?? ''}`;
      const plain = norm(text);
      const tight = squash(text);
      return words.every((w) => plain.includes(w) || tight.includes(w));
    });
  }, [boxes, typed, exact, filtering]);
  const offerNew = typed !== '' && !exact && filtering;
  const rows = shown.length + (offerNew ? 1 : 0);

  useEffect(() => { setActive(0); }, [typed, open]);
  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function pick(b: BoxChoice) {
    onPick(b);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setFiltering(false); setOpen(true); }
      else setActive((a) => Math.min(a + 1, rows - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      if (shown[active]) pick(shown[active]!);
      else setOpen(false); // "+ New box": the typed name stands.
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    }
  }

  const listId = id ? `${id}-list` : undefined;
  const showList = open && !disabled && (rows > 0);

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={(e) => { setFiltering(false); setOpen(true); e.currentTarget.select(); }}
        onClick={() => { setFiltering(false); setOpen(true); }}
        onBlur={() => setOpen(false)}
        onChange={(e) => { onType(e.target.value); setFiltering(true); setOpen(true); }}
        onKeyDown={onKeyDown}
        style={{ width: '100%' }}
      />
      {showList && (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          style={{
            position: 'absolute', zIndex: 40, left: 0, minWidth: '100%', width: 'max(100%, 420px)',
            top: 'calc(100% + 4px)', margin: 0, padding: 4, listStyle: 'none', maxHeight: 300,
            overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--line)',
            borderRadius: 8, boxShadow: '0 12px 28px rgba(0, 0, 0, .35)',
          }}
        >
          {shown.map((b, i) => (
            <li
              key={b.preset_id}
              role="option"
              aria-selected={b.box_name === value}
              // mousedown, not click: picking must happen before the input's
              // blur closes the list.
              onMouseDown={(e) => { e.preventDefault(); pick(b); }}
              onMouseEnter={() => setActive(i)}
              style={{
                padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                background: i === active ? 'var(--row-hover)' : undefined,
              }}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                  {b.box_name === value && <span aria-hidden="true" style={{ color: 'var(--ok)' }}>✓ </span>}
                  {b.box_name}
                </span>
                {b.code && <span className="dim mono" style={{ fontSize: 12 }}>{b.code}</span>}
              </div>
              <div className="dim" style={{ fontSize: 12 }}>{describe(b)}</div>
            </li>
          ))}
          {offerNew && (
            <li
              role="option"
              aria-selected={false}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); }}
              onMouseEnter={() => setActive(shown.length)}
              style={{
                padding: '7px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13.5,
                color: 'var(--accent)',
                background: active === shown.length ? 'var(--row-hover)' : undefined,
                borderTop: shown.length ? '1px solid var(--line-soft)' : undefined,
              }}
            >
              + New box “{typed}”
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
